/* ═══════════════════════════════════════════════════════════════════════════
   Night Feed — shared layout primitives.

   Before this file, six landing sections each hand-rolled the same
   eyebrow → headline → lede block. They had already drifted apart in spacing,
   type size and colour. Every section now composes from here instead, so a
   change lands everywhere at once and pages cannot silently diverge.

   Rules these primitives enforce (see DESIGN.md):
     · Gold is only ever used for something clickable, plus the logo.
       Signal (teal) is only ever used for a measured fact.
     · Type comes from the scale. A component picks `text-h2`; it does not pick
       a size, a weight and a tracking separately.
     · One section rhythm, one edge, square corners, nothing under 12px.
     · Motion is transform/opacity only, fires once, and lands on the CTA —
       and never gates content behind an entrance that might not fire.
   ═══════════════════════════════════════════════════════════════════════════ */

import { createContext, forwardRef, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";

/* ── The one conversion ─────────────────────────────────────────────────────
   The site was asking for the same thing under seven different names — "Get a
   free diagnosis", "Get a free creative diagnosis", "Start a creative sprint",
   "Brief us directly", "Send the brief", "Let's talk", "Make Something Like
   This". Each is fine alone; together they read as seven separate offers and
   the visitor has to work out they are all the same door.

   One name, imported everywhere. The form's own submit button is deliberately
   NOT this string — a submit button should describe the submit. */
export const CONVERSION = {
  label: "Get a free sample",
  // 13 of the 16 routes mount <Contact id="contact">; privacy, terms, 404 and
  // the three case studies do not. So a single hard-coded href cannot be right
  // everywhere: "#contact" is a dead anchor on the routes without the form, and
  // "/#contact" abandons the page a reader is already converting on. Use
  // <ConversionCTA>, which picks at click time.
  to: "/#contact",
  hash: "#contact",
};

/* ── Motion ─────────────────────────────────────────────────────────────────
   The whole site animates from this vocabulary. Four rules keep it from
   turning into decoration:

   1. transform + opacity only, so every animation stays on the compositor and
      never triggers layout. Motion that costs frames costs conversions.
   2. Entrances fire once, and they fire EARLY. The audit caught 63 elements
      sitting below 15% opacity at a normal scroll position on a seven-viewport
      page — the reveal was finishing after the reader had already arrived.
      400ms, 12px of travel, and a threshold that trips as the element enters
      rather than when it is half-way up the screen.
   3. Stagger is capped at three items. Past three, the last one lands late
      enough to read as broken rather than as sequence.
   4. Reduced motion is honoured HERE, in JS. `MotionConfig reducedMotion="user"`
      in App.js suppresses transforms but deliberately preserves opacity
      animation, and the `prefers-reduced-motion` block in index.css can only
      reach CSS keyframes — neither can stop a framer-motion element from
      starting at opacity 0. `useReveal` reads the media query itself and
      starts revealed. */

export const EASE = [0.2, 0.7, 0.2, 1];
export const REVEAL_S = 0.4;
export const STAGGER_S = 0.06;
export const MAX_STAGGER = 3;

export const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ── useReveal ──────────────────────────────────────────────────────────────
   Entrance triggering that cannot strand content.

   framer-motion's `whileInView` is IntersectionObserver-only. An element that
   enters and leaves the root between two IO callbacks never fires, and because
   the entrance starts at opacity:0 that content stays invisible permanently.
   Testing this page at a narrow viewport left ten blocks stuck that way.

   This pairs the observer with a geometry check that scrolling cannot outrun,
   and runs once on mount so anything already on screen shows immediately.
   Either path latches `shown` to true and both then detach. */
export function useReveal() {
  const ref = useRef(null);
  // Anyone who asked for reduced motion starts revealed — no observer, no
  // transform, no opportunity to be stranded.
  const [shown, setShown] = useState(prefersReducedMotion);

  useEffect(() => {
    const el = ref.current;
    if (!el || shown) return undefined;

    const near = () => {
      const r = el.getBoundingClientRect();
      // Trip as the element enters the viewport, not when it is half-way up:
      // the reveal should be FINISHED by the time the reader is looking at it.
      if (r.top < window.innerHeight * 0.98 && r.bottom > -120) setShown(true);
    };

    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setShown(true); },
      { rootMargin: "0px 0px -12% 0px", threshold: 0 }
    );
    io.observe(el);

    // Deliberately NOT rAF-gated. rAF can be starved (background tabs,
    // throttled renderers, headless virtual time) and a missed frame here
    // means content never appears — the exact failure this hook exists to
    // prevent. One getBoundingClientRect per scroll event is cheap, and every
    // listener detaches the moment its element has been revealed.
    window.addEventListener("scroll", near, { passive: true });
    window.addEventListener("resize", near, { passive: true });
    near();

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", near);
      window.removeEventListener("resize", near);
    };
  }, [shown]);

  return [ref, shown];
}

/* ── Reveal / RevealItem ────────────────────────────────────────────────────
   An entrance that CANNOT strand content. That is the whole requirement, and
   the previous implementation failed it in a way that was easy to miss.

   It used framer-motion's variant propagation: the parent carried
   `animate={shown ? "shown" : "hidden"}` plus a container variant holding only
   a `staggerChildren` transition, and each child defined `hidden`/`shown` and
   inherited the label implicitly. Parked with a section fully in view, the
   children stayed at `opacity: 0` — the parent's label never reached them,
   while every ancestor sat at opacity 1. Content that is on screen and
   invisible is the exact failure this component exists to prevent, and
   implicit propagation gives you no way to see it happening.

   So the parent now publishes `shown` through React context and each item
   animates ITSELF. More code, no magic, and one useful property: an item
   rendered with no Reveal above it defaults to VISIBLE. If this ever breaks
   again it breaks toward showing content, not hiding it. */
const RevealCtx = createContext(null);

export const Reveal = ({ delay = 0, gap = STAGGER_S, className = "", children, as: Tag = "div", ...rest }) => {
  const [ref, shown] = useReveal();
  const M = motion[Tag] || motion.div;
  const ctx = useMemo(() => ({ shown, delay, gap }), [shown, delay, gap]);
  return (
    <RevealCtx.Provider value={ctx}>
      <M ref={ref} className={className} {...rest}>
        {children}
      </M>
    </RevealCtx.Provider>
  );
};

/* One item inside a Reveal. Pass `index` in a list to stagger it; the delay
   stops accumulating after the third item, because past three the tail arrives
   late enough to read as broken rather than as sequence. */
export const RevealItem = ({ index = 0, className = "", children, as: Tag = "div", ...rest }) => {
  const ctx = useContext(RevealCtx);
  const shown = ctx ? ctx.shown : true;
  const delay = ctx ? ctx.delay + Math.min(index, MAX_STAGGER) * ctx.gap : 0;
  const M = motion[Tag] || motion.div;
  return (
    <M
      className={className}
      initial={{ opacity: 0, y: 12 }}
      animate={shown ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
      transition={{ duration: REVEAL_S, ease: EASE, delay: shown ? delay : 0 }}
      {...rest}
    >
      {children}
    </M>
  );
};

/* Page-load sequence for the hero. Index drives the delay so the order is
   declared where it's read, not scattered across transition props. Under
   reduced motion this collapses to a plain fade with no delay — the hero is
   the first thing on the page and must never wait on an animation. */
export const enter = (i = 0) => {
  if (prefersReducedMotion()) {
    return { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.2 } };
  }
  return {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: REVEAL_S, delay: 0.05 + i * 0.06, ease: EASE },
  };
};

/* Type that arrives from behind its own baseline. Used once, on the hero.
   The reduced-motion branch is explicit and load-bearing: `initial` is itself
   a transform, so MotionConfig's transform suppression would leave the line
   parked at y:115% inside its overflow-hidden wrapper — permanently invisible.
   The whole point of a reduced-motion path is that it cannot hide content. */
export const clipUp = (i = 0) => {
  if (prefersReducedMotion()) {
    return { initial: { opacity: 1, y: 0 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0 } };
  }
  return {
    initial: { y: "110%" },
    animate: { y: 0 },
    transition: { duration: 0.55, delay: 0.06 + i * 0.05, ease: EASE },
  };
};

/* ── Eyebrow ────────────────────────────────────────────────────────────────
   Section eyebrows and data captions are the ONLY two things allowed to use
   the mono label. It appeared 217 times on the homepage — 163 of those at 9px
   — which is the point at which an accent stops being an accent and becomes
   texture. The tick is neutral: this is not a brand moment. */
export const Eyebrow = ({ children, className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <span aria-hidden="true" className="h-px w-8 shrink-0 bg-line" />
    <span className="font-label text-label uppercase text-faint">
      {children}
    </span>
  </div>
);

/* ── Rule ───────────────────────────────────────────────────────────────── */
export const Rule = ({ className = "" }) => (
  <div aria-hidden="true" className={`hairline w-full ${className}`} />
);

/* ── Section ────────────────────────────────────────────────────────────────
   Owns vertical rhythm and the max-width container so individual sections stop
   inventing their own padding. The page had four rhythms (112/112, 96/56,
   80/80 and 0/0); it has one now — 96px on phones, 128px on desktop.

   `tone="deep"` used to swap in a second near-black band to separate sections.
   At 1.08:1 that separated nothing while costing a token, so it draws a rule
   instead. Same prop, same call sites, a separator you can actually see. */
export const Section = forwardRef(({
  id,
  tone = "ink",
  size = "default",
  className = "",
  containerClassName = "",
  children,
  ...rest
}, ref) => {
  const rhythm = size === "tight" ? "py-16 md:py-20" : "py-24 md:py-32";
  // tone="deep" is a real band again. It was demoted to a bare top rule when
  // the ground was near-black and a second tone measured 1.08:1 — separating
  // nothing. Against the charcoal ground `surface` reaches 1.29:1, so
  // alternating bands give the page tonal rhythm instead of leaving spacing to
  // do the entire job of telling one section from the next.
  const band = tone === "deep" ? "bg-surface border-y border-rule" : "bg-ground";
  return (
    <section
      ref={ref}
      id={id}
      className={`relative ${band} ${rhythm} ${className}`}
      {...rest}
    >
      <div className={`mx-auto w-full max-w-7xl px-6 md:px-10 ${containerClassName}`}>
        {children}
      </div>
    </section>
  );
});
Section.displayName = "Section";

/* ── PageHero ───────────────────────────────────────────────────────────────
   The 13 sub-pages each hand-rolled this block — `pt-40 pb-24`, an h1 at
   `font-black text-5xl md:text-7xl tracking-tighter`, and their own fade. That
   is why none of them inherited a single fix. The nav offset is a separate job
   from the section rhythm, so it lives here and only here. */
export const PageHero = ({ eyebrow, title, lede, children, className = "" }) => (
  <section className={`relative bg-ground pb-20 pt-32 md:pb-24 md:pt-40 ${className}`}>
    <div className="mx-auto w-full max-w-7xl px-6 md:px-10">
      <Reveal>
        {eyebrow && <RevealItem><Eyebrow className="mb-6">{eyebrow}</Eyebrow></RevealItem>}
        <RevealItem>
          <h1 className="font-display text-display uppercase text-bone">{title}</h1>
        </RevealItem>
        {lede && (
          <RevealItem as="p" className="measure mt-7 font-body text-body-l text-mute">
            {lede}
          </RevealItem>
        )}
      </Reveal>
      {/* Outside the Reveal on purpose. `children` here is the page's primary
          CTA, and the rule is that a primary action is never gated behind an
          entrance — not even one that resolves on mount. */}
      {children && <div className="mt-10">{children}</div>}
    </div>
  </section>
);

/* ── SectionHeader ──────────────────────────────────────────────────────────
   layout="split" puts the lede opposite the heading on desktop;
   layout="stack" keeps it underneath for narrower sections.

   `accent` is a typographic line break, not a colour change. An earlier pass
   rendered every accent line in the brand colour, which recreated the old
   template (line one white, line two neon) in a new palette — six sections
   opening identically. There is no coloured-headline option any more: gold is
   for things you can click, and a headline is not one of them. */
export const SectionHeader = ({
  eyebrow,
  title,
  accent,
  lede,
  layout = "split",
  size = "lg",
  className = "",
}) => (
  <Reveal className={`mb-14 ${className}`}>
    <RevealItem><Rule className="mb-8" /></RevealItem>
    <div
      className={
        layout === "split"
          ? "flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
          : "max-w-3xl"
      }
    >
      <RevealItem className="min-w-0">
        {eyebrow && <Eyebrow className="mb-5">{eyebrow}</Eyebrow>}
        <h2 className={`font-display uppercase text-bone ${size === "lg" ? "text-h1" : "text-h2"}`}>
          {title}
          {accent && (
            <>
              <br />
              <span>{accent}</span>
            </>
          )}
        </h2>
      </RevealItem>
      {lede && (
        <RevealItem
          as="p"
          className={
            // The narrow right-aligned column is a SPLIT-layout treatment: it
            // only makes sense sitting opposite the heading. Applied under
            // layout="stack" it rendered the lede as a narrow right-aligned
            // strip under a left-aligned heading.
            layout === "split"
              ? "max-w-sm font-body text-body text-mute md:text-right"
              : "measure mt-5 font-body text-body text-mute"
          }
        >
          {lede}
        </RevealItem>
      )}
    </div>
  </Reveal>
);

/* ── Panel ──────────────────────────────────────────────────────────────────
   Every card on the site asked for BOTH an outline and a raised surface, and
   got neither: `--line` measured 1.35:1 against the ground and the raised fill
   1.08:1. On a ground this dark, tint cannot do elevation — a surface would
   have to climb to about #352F29 to reach even 1.5:1, which turns the whole
   page grey. So cards are defined by their outline and carry no fill at all.

   `interactive` brightens the edge to gold on hover, and is only correct when
   the whole panel is genuinely a link or a button. Several pages put a
   hover-border on cards that do nothing, which is a false affordance. */
export const Panel = ({ interactive = false, className = "", children, as: Tag = "div", ...rest }) => (
  <Tag
    className={
      // Fill AND edge. Cards were outline-only because a tint could not be seen
      // on the old near-black ground; on charcoal the fill reads, and a card
      // that is a distinct surface beats a card that is a rectangle of nothing.
      `border border-line bg-surface p-6 md:p-8 transition-colors duration-hover ease-gate ` +
      `${interactive ? "hover:border-gold" : ""} ${className}`
    }
    {...rest}
  >
    {children}
  </Tag>
);

/* ── CardTitle ──────────────────────────────────────────────────────────────
   Weight decreases as size increases. The site had this backwards: an 18px
   card title at weight 900 next to a 68px hero at weight 400, so the small
   type shouted and the display looked thin. `text-h3` carries weight 600. */
export const CardTitle = ({ children, className = "", as: Tag = "h3", ...rest }) => (
  <Tag className={`font-headline text-h3 text-bone ${className}`} {...rest}>
    {children}
  </Tag>
);

/* ── CTA ────────────────────────────────────────────────────────────────────
   Two variants. That is the whole set.

   `to` renders a router Link, `href` renders an anchor — both keep Cmd- and
   middle-click working, which a <button> with an onClick handler would not.

   Note `size` changes horizontal padding only. Every button on the site is
   48px tall with a 16px label; a second type size for buttons is exactly the
   kind of local decision that produced sixteen font sizes. */
// Height and padding deliberately live in the size/tone strings, NOT here.
// Tailwind resolves conflicting utilities by stylesheet order, not by the order
// they appear in a className, so a `px-0` added later cannot beat a `px-8` in
// the base — it just silently loses, and `ghost` renders as a padded 48px block.
// `whitespace-nowrap` is load-bearing: a 16px uppercase label at 0.12em is wide,
// and in a narrow hero column "Get a free sample" wrapped to two lines, which
// made the primary action look like a paragraph. A button label is one line.
const ctaBase =
  "group inline-flex items-center justify-center gap-3 whitespace-nowrap font-label text-body " +
  "font-medium uppercase tracking-action transition-colors duration-hover ease-gate";

const ctaSizes = {
  md: "min-h-12 px-6",
  lg: "min-h-12 px-8",
};

const ctaTones = {
  // Gold, and clickable. The two are the same statement.
  primary: "bg-gold text-gold-on hover:bg-gold-hover",
  secondary: "border border-line text-bone hover:border-gold hover:text-gold",
  // For use inside the gold conversion band, where a gold button would vanish.
  inverse: "bg-ground text-bone hover:bg-surface",
  ghost: "text-bone hover:text-gold",
};

export const CTA = ({
  to,
  href,
  tone = "primary",
  size = "lg",
  arrow = true,
  children,
  className = "",
  ...rest
}) => {
  const cls = `${ctaBase} ${tone === "ghost" ? "" : ctaSizes[size]} ${ctaTones[tone]} ${className}`;
  const inner = (
    <>
      {children}
      {arrow && (
        <ArrowRight
          aria-hidden="true"
          className="h-4 w-4 transition-transform duration-hover ease-gate group-hover:translate-x-1"
        />
      )}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={cls} {...rest}>
        {inner}
      </Link>
    );
  }
  return (
    <a href={href} className={cls} {...rest}>
      {inner}
    </a>
  );
};

/* ── ConversionCTA ──────────────────────────────────────────────────────────
   The one action, pointed at the right place from wherever it is rendered.

   The site asks for the same thing from the navbar, the footer band, the work
   modal, three shared sections and thirteen pages — but only some of those
   routes mount the form. Hard-coding "#contact" leaves a dead anchor on the
   case studies; hard-coding "/#contact" throws a reader off the page they were
   already converting on. Both were live in the codebase at once.

   So the href is the cross-route one — which is the correct fallback, and keeps
   Cmd-click and "open in new tab" working — and the click handler short-
   circuits to a scroll when this page happens to have the form. */
export const ConversionCTA = ({ children, onClick, ...rest }) => {
  const handle = (e) => {
    onClick?.(e);
    // Let the browser do its normal thing for modified clicks and middle-click.
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    const target = document.getElementById("contact");
    if (!target) return;                    // no form here — travel to the homepage
    e.preventDefault();
    target.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth" });
    window.history?.replaceState?.(null, "", CONVERSION.hash);
  };

  return (
    <CTA to={CONVERSION.to} onClick={handle} {...rest}>
      {children ?? CONVERSION.label}
    </CTA>
  );
};

/* ── ConversionBand ─────────────────────────────────────────────────────────
   The full-bleed gold block was the only saturated colour plane on a
   seven-viewport page and it arrived as a hard cut from near-black — nothing
   prepared you for it. It appeared at three different heights in seven places,
   and on the case studies it was the second thing on screen and not even
   clickable.

   One component, one height, and a lead-in: a rule and a step of ground before
   the plane, so the page builds toward it instead of jump-cutting. */
export const ConversionBand = ({
  title = "Tell us what you're trying to grow.",
  lede,
  label = CONVERSION.label,
  // Pass `to` only to send the band somewhere OTHER than the conversion — the
  // Education page points its band at a case study. Left undefined (the normal
  // case) the button resolves the conversion itself, per route.
  to,
}) => (
  <div className="bg-ground">
    <div className="mx-auto w-full max-w-7xl px-6 md:px-10">
      <Rule />
    </div>
    <div className="h-16 md:h-24" aria-hidden="true" />
    <div className="bg-gold text-gold-on">
      <div className="mx-auto w-full max-w-7xl px-6 py-20 md:px-10 md:py-24">
        {/* Measured at 1440: the old `items-end` hung the button 24px below the
            text block's optical centre, and `justify-between` against an
            unconstrained headline left only a 47px gutter — the headline's last
            word nearly touched the button. Content ended up in the top-left and
            bottom-right corners with a diagonal void between them.

            Capping the text column forces the headline onto two lines, which
            reads as a block rather than a strip, and leaves a real gutter for
            the button to sit in, centred against it. */}
        <Reveal className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between md:gap-16">
          <RevealItem className="min-w-0 md:max-w-3xl">
            <h2 className="font-display text-h1 uppercase">{title}</h2>
            {lede && <p className="measure mt-5 font-body text-body-l opacity-80">{lede}</p>}
          </RevealItem>
          <RevealItem className="shrink-0">
            {to ? (
              <CTA tone="inverse" to={to}>{label}</CTA>
            ) : (
              <ConversionCTA tone="inverse">{label}</ConversionCTA>
            )}
          </RevealItem>
        </Reveal>
      </div>
    </div>
  </div>
);

/* ── FilmEmbed ──────────────────────────────────────────────────────────────
   The case studies sold film-grade craft and showed no film. Three pages of
   prose about cinematic production, and not one frame — which asks the reader
   to take the single most checkable claim entirely on trust.

   This puts the work directly under the headline, where it does the qualifying
   before any of the copy has to.

   It is a facade, not an eager embed: the poster is a local still and the
   Vimeo iframe is only created on click. Three case studies loading a player
   each on arrival would cost more than the work is worth, and none of those
   players would be watched by most visitors.

   `caption` sits BELOW the frame, never on it — same rule as the work grid.
   The still is the product; nothing gets painted over it. */
const FILM_RATIO = {
  video: "aspect-video",       // 16:9 — brand films
  square: "aspect-square",     // 1:1  — social-native performance cuts
  reel: "aspect-reel",         // 9:16 — vertical drama
  brand: "aspect-brand",       // 4:5
};

const FILM_WIDTH = {
  video: "",
  square: "max-w-2xl",
  reel: "max-w-sm",
  brand: "max-w-md",
};

export const FilmEmbed = ({ poster, alt, videoId, aspect = "video", caption, label }) => {
  const [playing, setPlaying] = useState(false);
  const ratio = FILM_RATIO[aspect] || FILM_RATIO.video;

  return (
    <figure className={`w-full ${FILM_WIDTH[aspect] ?? ""}`}>
      <div className={`relative overflow-hidden border border-line bg-surface ${ratio}`}>
        {playing ? (
          <iframe
            title={label || "Film"}
            src={`https://player.vimeo.com/video/${videoId}?autoplay=1&title=0&byline=0&portrait=0`}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
        ) : (
          <>
            <img
              src={poster}
              alt={alt}
              width={960}
              height={540}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover"
            />
            {videoId && (
              <button
                type="button"
                onClick={() => setPlaying(true)}
                aria-label={label ? `Play ${label}` : "Play the film"}
                /* Neutral at rest, gold on hover: it IS clickable, so gold is
                   earned — but a permanently gold disc over the frame competes
                   with the page's real conversion, which is the mistake the
                   work grid used to make thirty-three times over. */
                className="group absolute inset-0 flex items-center justify-center"
              >
                <span className="flex h-14 w-14 items-center justify-center border border-line bg-ground/80 text-bone transition-colors duration-hover ease-gate group-hover:border-gold group-hover:text-gold">
                  <Play aria-hidden="true" className="h-5 w-5" />
                </span>
              </button>
            )}
          </>
        )}
      </div>
      {caption && (
        <figcaption className="mt-3 font-body text-caption text-faint">{caption}</figcaption>
      )}
    </figure>
  );
};

/* ── Stat ───────────────────────────────────────────────────────────────────
   Figures are the one thing on this site that must never look invented, so
   they are set in mono with tabular figures, carry their source, and are the
   only place the signal colour appears outside a chart. Teal here is doing the
   job the whole token exists for: this is a measured fact. */
export const Stat = ({ value, label, source }) => (
  <Reveal gap={0.06} className="flex flex-col gap-2">
    {/* A clip reveal rather than a count-up: it works for "30-60%" and "48 hrs"
        alike, and doesn't imply a number is being tallied live. RevealItem
        rather than bare variants — these three spans were among the elements
        left stranded at opacity 0 by the old propagation, and the figure is the
        single most important thing in the component. */}
    <span className="block overflow-hidden">
      <RevealItem as="span" className="block font-label text-h1 font-medium tabular-nums text-signal">
        {value}
      </RevealItem>
    </span>
    <RevealItem as="span" index={1} className="block font-body text-body text-bone">
      {label}
    </RevealItem>
    {source && (
      <RevealItem as="span" index={2} className="block font-label text-label uppercase text-faint">
        {source}
      </RevealItem>
    )}
  </Reveal>
);
